<template>
  <div>
    <v-row no-gutters>
      <v-col cols="12" sm="1">
        <router-link :to="authorRoute">
          <v-avatar size="40">
            <img class="primary" :src="profilePicture">
          </v-avatar>
        </router-link>
      </v-col>
      <v-col cols="12" sm="11">
        <v-card
          class="ml-2 px-4 py-2"
          :color="comment.isChoice ? '#FFEDE8' : '#F6F6F6'"
          outlined
          style="border-radius: 16px"
        >
          <div>
            <router-link :to="authorRoute" class="fb-link ma-0 trim-oneline">
              {{ authorName }}
            </router-link>
            <FbText
              class="ma-0"
              :max-lines="8"
              :text="comment.content" />
            <img
              class="img mt-2"
              v-if="comment.picture"
              :src="comment.picture"
              @click="window.open(comment.picture, '_blank')" />
            <FbWebLink
              class="my-2"
              v-else-if="comment.link"
              :url="comment.link" />
          </div>
          <div 
            v-if="editMode"
            class="py-2"
          >
            <v-divider class="mb-4" />
            <FbMemberAutocomplete
              v-model="comment.author"
              class="mb-2"
              label="Author"
              outlined />
            <v-row>
              <v-col cols="12" sm="6" class="pr-2">
                <v-dialog
                  ref="dateDialog"
                  v-model="dateDialog"
                  :return-value.sync="date"
                  persistent
                  width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="date"
                      label="Date"
                      readonly
                      hide-details
                      outlined
                      v-on="on" />
                  </template>
                  <v-date-picker v-model="date" scrollable>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="dateDialog = false">Cancel</v-btn>
                    <v-btn text color="primary" @click="$refs.dateDialog.save(date)">OK</v-btn>
                  </v-date-picker>
                </v-dialog>
              </v-col>
              <v-col cols="12" sm="6" class="pl-2">
                <v-dialog
                  ref="timeDialog"
                  v-model="timeDialog"
                  :return-value.sync="time"
                  persistent
                  width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="time"
                      label="Time"
                      readonly
                      hide-details
                      outlined
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="time"
                    full-width
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="timeDialog = false">Cancel</v-btn>
                    <v-btn text color="primary" @click="$refs.timeDialog.save(time)">OK</v-btn>
                  </v-time-picker>
                </v-dialog>
              </v-col>
            </v-row>
            <v-row no-gutters class="mt-1 mb-5">
              <v-col cols="12" sm="3" class="pr-2">
                <v-btn color="primary" outlined width="100%" @click="incrementTime(1)">+1m</v-btn>
              </v-col>
              <v-col cols="12" sm="3" class="px-2">
                <v-btn color="primary" outlined width="100%" @click="incrementTime(5)">+5m</v-btn>
              </v-col>
              <v-col cols="12" sm="3" class="px-2">
                <v-btn color="primary" outlined width="100%" @click="incrementTime(30)">+30m</v-btn>
              </v-col>
              <v-col cols="12" sm="3" class="pl-2">
                <v-btn color="primary" outlined width="100%" @click="incrementTime(3*60)">+3h</v-btn>
              </v-col>
            </v-row>
            <FbTextField
              textarea
              outlined
              no-resize
              class="mb-2"
              v-model="comment.content"
              label="Content" />
            <v-row>
              <v-col cols="12" sm="6">
                <v-btn
                  outlined
                  width="100%"
                  :color="comment.picture ? 'error' : 'primary'"
                  @click="comment.picture ? comment.picture = '' : $refs.imageUploadDlg.show()"
                >
                  <v-icon left>mdi-image</v-icon>
                  {{ comment.picture ? 'Remove' : 'Photo' }}
                </v-btn>
              </v-col>
              <v-col cols="12" sm="6">
                <v-btn
                  outlined
                  width="100%"
                  :color="comment.link ? 'error' : 'primary'"
                  @click="comment.link ? comment.link = '' : linkDialog = { show: true, url: '' }"
                >
                  <v-icon left>mdi-link</v-icon>
                  {{ comment.link ? 'Remove' : 'Link' }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" :sm="reply ? 12 : 6">
                <v-text-field
                  v-model.number="comment.likeCount"
                  label="Likes"
                  hide-details
                  outlined />
              </v-col>
              <v-col cols="12" sm="6" v-if="!reply">
                <v-text-field
                  v-model.number="comment.replyCount"
                  label="Replies (fake if max)"
                  hide-details
                  outlined />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="4">
                <v-checkbox
                  v-model="comment.likeable"
                  label="Likeable"
                  hide-details
                  class="ma-0 pa-0" />
              </v-col>
              <v-col cols="12" sm="4">
                <v-checkbox
                  v-model="comment.liked"
                  label="Liked"
                  hide-details
                  class="ma-0 pa-0" />
              </v-col>
              <v-col cols="12" sm="4">
                <v-checkbox
                  v-model="comment.isChoice"
                  label="Choice"
                  hide-details
                  class="ma-0 pa-0" />
              </v-col>
            </v-row>
            <v-text-field
              v-if="comment.isChoice"
              class="mt-2"
              v-model="comment.choicePreview"
              label="Choice preview"
              outlined
              hide-details />
          </div>
        </v-card>
        <v-layout row class="mt-1 ml-4">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <p class="body-2 text--secondary ma-0 mr-4" v-on="on" v-bind="attrs">
                {{ comment.isChoice ? 'Player choice' : timestamp }}
              </p>
            </template>
            <v-list>
              <v-list-item
                v-for="(item, index) in items"
                :key="index"
              >
                <v-list-item-title @click="item.action">{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <a
            :class="`
              body-2
              font-weight-bold 
              ${liked ? 'text--accent': 'text--secondary'} 
              ma-0 mr-4
            `"
            @click="comment.liked = !comment.liked"
          >
            Like {{ likeCount ? `(${commaify(likeCount)})` : '' }}
          </a>
          <a
            v-if="!reply"
            class="body-2 font-weight-bold text--secondary ma-0 mr-4"
            @click="makeNewReply"
          >
            Reply {{ replyCount ? `(${commaify(replyCount)})` : '' }}
          </a>
          <a
            :class="`
              body-2
              font-weight-bold 
              ${editMode ? 'text--accent': 'text--secondary'} 
              ma-0 mr-4
            `"
            @click="editMode = !editMode"
          >
            Edit
          </a>
          <a
            class="body-2 font-weight-bold text--secondary ma-0"
            @click="$emit('delete', comment.id)"
          >
            Delete
          </a>
        </v-layout>
        <div v-if="!reply">
          <FbComment
            v-for="item in sortedReplies"
            :key="item.id"
            :comment="item.reply"
            class="mt-5"
            reply
            @delete="$emit('delete-reply', comment.id, item.id)" />
        </div>
      </v-col>
    </v-row>
    <ImageUploadDialog
      ref="imageUploadDlg"
      @upload="url => { comment.picture = url; comment.link = '' }" />
    <v-dialog
      v-model="linkDialog.show"
      max-width="400"
    >
      <v-card>
        <v-card-title class="headline">Input URL</v-card-title>
        <v-text-field
          v-model="linkDialog.url"
          class="ma-0 pa-0 mx-6"
          placeholder="https://"
          hide-details />
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            @click="linkDialog.show = false"
          >
            Cancel
          </v-btn>
          <v-btn
            text
            color="primary"
            :disabled="Store.getWebPage(linkDialog.url) == null"
            @click="comment.link = linkDialog.url; comment.picture = ''; linkDialog.show = false"
          >
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import copy from 'copy-to-clipboard'

import FbText from '../components/FbText'
import FbTextField from '../components/FbTextField'
import FbWebLink from '../components/FbWebLink'
import FbMemberAutocomplete from '../components/FbMemberAutocomplete'
import ImageUploadDialog from '../components/ImageUploadDialog'

import FbMemberMixin from '../mixins/fb-member'
import { vueObjectAssign, commaify, fbCommentLikeCount, randomId } from '../utils'
import { randomSeed } from '../random-fb-profile'

export default {
  name: 'FbComment',
  mixins: [FbMemberMixin],
  components: {
    FbText,
    FbTextField,
    FbWebLink,
    FbMemberAutocomplete,
    ImageUploadDialog
  },
  props: {
    comment: Object,
    reply: Boolean
  },
  created() {
    vueObjectAssign(this.comment, {
      timestamp: 1388534400000,
      author: '',
      likeCount: 0,
      likeable: true,
      liked: false,
      isChoice: false,
      content: 'Comment here',
      choicePreview: '',
      replyCount: 0,
      replies: {},
      picture: '',
      link: ''
    })
  },
  data() {
    return {
      editMode: false,
      dateDialog: false,
      timeDialog: false,
      linkDialog: {
        show: false,
        url: ''
      },
      window,
      items: [
        { title: 'Copy', action: this.copyTimestamp },
        { title: 'Paste', action: this.pasteTimestamp },
      ],
    }
  },
  computed: {
    timestamp() {
      return moment.utc(this.comment.timestamp).format('D MMM YYYY [at] h:mm a')
    },
    date: {
      get() {
        return moment.utc(this.comment.timestamp).format().substring(0, 10)
      },
      set(value) {
        value = moment.utc(value, 'YYYY-MM-DD')
        value = moment.utc(this.comment.timestamp)
          .year(value.year())
          .month(value.month())
          .date(value.date())
        this.comment.timestamp = value.valueOf()
      }
    },
    time: {
      get() {
        return moment.utc(this.comment.timestamp).format('HH:mm')
      },
      set(value) {
        value = moment.utc(value, 'HH:mm')
        value = moment.utc(this.comment.timestamp)
          .hour(value.hour())
          .minute(value.minute())
          .second(value.second())
        this.comment.timestamp = value.valueOf()
      }
    },
    liked() {
      return this.comment.likeable && this.comment.liked
    },
    likeCount() {
      return fbCommentLikeCount(this.comment)
    },
    replyCount() {
      return Math.max(
        this.comment.replyCount,
        Object.keys(this.comment.replies).length
      )
    },
    memberResult() {
      return this.memberFromRef(this.comment.author)
    },
    profilePicture() {
      const { member = {} } = this.memberResult || {}
      return member.profilePicture || ''
    },
    authorRoute() {
      const { route = '' } = this.memberResult || {}
      return route
    },
    authorName() {
      const { member } = this.memberResult || {}
      return member ? this.name(member) : '(no author)'
    },
    sortedReplies() {
      const items = []
      for (let id in this.comment.replies) {
        const reply = this.comment.replies[id]
        items.push({ id, reply })
      }
      return items.sort((a, b) => a.reply.timestamp > b.reply.timestamp)
    }
  },
  methods: {
    commaify,
    copyTimestamp() {
      this.$set(this.Store, 'timestampClipboard', this.comment.timestamp)
    },
    pasteTimestamp() {
      this.comment.timestamp = this.Store.timestampClipboard
    },
    makeNewReply() {
      const { replies } = this.comment
      const latestReply = Object.keys(replies)
        .map(id => replies[id])
        .sort((a, b) => a.timestamp < b.timestamp)[0] || {}
      const timestamp = latestReply.timestamp || this.comment.timestamp

      const maleAuthor = Math.random() < 0.5
      const author = `@R:${randomSeed(maleAuthor)}`

      const newReplies = {
        ...replies,
        [randomId()]: { author, timestamp }
      }
      this.comment.replies = newReplies
    },
    incrementTime(mins) {
      const { timestamp = 0 } = this.comment
      this.comment.timestamp = moment.utc(timestamp).add(mins, 'm').valueOf()
    },
    copyIdToClipboard() {
      copy(this.comment.id)
    }
  }
}
</script>

<style scoped>
.img {
  display: block;
  width: 100%;
  max-height: 480px;
  object-fit: cover;
  cursor: pointer;
  transition: filter 0.15s;
}

.img:hover {
  filter: brightness(90%);
}
</style>